import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { API_BASE, apiFetch } from '../lib/api'

export type AuthRole = 'admin' | 'agent'

export type AuthUser = {
    id: number
    name: string
    role: AuthRole
}

type AuthStatus = 'loading' | 'authed' | 'guest'

type AuthContextValue = {
    status: AuthStatus
    user: AuthUser | null
    refreshAuth: () => Promise<void>
    setAuthUser: (user: AuthUser | null) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [status, setStatus] = useState<AuthStatus>('loading')
    const [user, setUser] = useState<AuthUser | null>(null)

    const refreshAuth = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_BASE}/api/auth/me`)
            const data = await res.json().catch(() => null)
            if (res.ok && (data?.user?.role === 'admin' || data?.user?.role === 'agent')) {
                setUser({
                    id: Number(data.user.id),
                    name: String(data.user.name),
                    role: data.user.role,
                })
                setStatus('authed')
                return
            }
        } catch {
            // noop
        }

        setUser(null)
        setStatus('guest')
    }, [])

    useEffect(() => {
        let active = true

        const load = async () => {
            await refreshAuth()
            if (!active) return
        }

        load()

        return () => {
            active = false
        }
    }, [refreshAuth])

    const value = useMemo<AuthContextValue>(
        () => ({
            status,
            user,
            refreshAuth,
            setAuthUser: (next) => {
                setUser(next)
                setStatus(next ? 'authed' : 'guest')
            },
        }),
        [status, user, refreshAuth]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext)
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return ctx
}
